import { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAlert } from "../../../actions/alert";
import { addVehicleType, getVehicleType } from "../../../actions/admin/vehicletype";
import { vehicleTypeImageOptions, iconTypes } from "../../../utils/dataLists";
import Alert from "../../layout/Alert";
import AdminPage from "../_components/AdminPage";
import {
  AdminFormCard,
  AdminFormFullRow,
  AdminTextField,
  AdminTextAreaField,
  AdminSelectField,
  AdminBackLink,
  AdminFormButton,
  AdminFormActions,
} from "../_components/AdminForm";
import ConfirmationModal from "../components/ConfirmationModal";
import SuccessModal from "../components/SuccessModal";
import { ChangeList, diffFields } from "../components/ChangeSummary";
import useSaveConfirmation from "../components/useSaveConfirmation";
import { VEHICLE_TYPE_LIST, VEHICLE_TYPE_FIELDS, AMENITY_SLOTS } from "./fields";

// date value stored on the vehicle type -> value an <input type="date"> accepts
const toDateInputValue = (value) => {
  const date = new Date(value);
  return isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const text = (loading, value) => (loading || !value ? "" : value);
const number = (loading, value) => (loading || !value ? 0 : value);

// The stored record as the form takes it. One normalizer fills the form *and* supplies
// the "before" side of the change summary, so the two cannot drift.
const vehicleTypeToFormData = (vehicletype, loading) => ({
  _id: text(loading, vehicletype?._id),
  typename: text(loading, vehicletype?.typename),
  vehicletypeimage:
    loading || !vehicletype?.vehicletypeimage
      ? vehicleTypeImageOptions[0].label
      : vehicletype.vehicletypeimage,
  hourlyprice: number(loading, vehicletype?.hourlyprice),
  hourlypriceOutsideTokyo: number(loading, vehicletype?.hourlypriceOutsideTokyo),
  baseprice: number(loading, vehicletype?.baseprice),
  basepricehours: number(loading, vehicletype?.basepricehours),
  basepriceOutsideTokyo: number(loading, vehicletype?.basepriceOutsideTokyo),
  basepricehoursOutsideTokyo: number(loading, vehicletype?.basepricehoursOutsideTokyo),
  description: text(loading, vehicletype?.description),
  maxpassenger: number(loading, vehicletype?.maxpassenger),
  maxluggages: number(loading, vehicletype?.maxluggages),
  subtitle: text(loading, vehicletype?.subtitle),
  lastAvailibility:
    loading || !vehicletype?.lastAvailibility
      ? ""
      : toDateInputValue(vehicletype.lastAvailibility),
  ...AMENITY_SLOTS.reduce(
    (acc, n) => ({
      ...acc,
      [`option${n}Icon`]:
        loading || !vehicletype?.[`option${n}Icon`]
          ? iconTypes[0].label
          : vehicletype[`option${n}Icon`],
      [`optionText${n}`]: text(loading, vehicletype?.[`optionText${n}`]),
    }),
    {}
  ),
});

const UpdateVehicleType = ({ match }) => {
  const dispatch = useDispatch();
  const { loading, vehicletype } = useSelector((state) => state.vehicletype);

  const [formData, setFormData] = useState(() => vehicleTypeToFormData(null, true));

  // The stored record as the form saw it when it loaded — the "before" side of the
  // change summary.
  const savedFormData = useRef(null);

  useEffect(() => {
    dispatch(getVehicleType(match.params.id));
  }, [dispatch, match.params.id]);

  useEffect(() => {
    const loadedFormData = vehicleTypeToFormData(vehicletype, loading);
    savedFormData.current = loadedFormData;
    setFormData(loadedFormData);
  }, [loading, vehicletype]);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const save = useSaveConfirmation({
    save: () => dispatch(addVehicleType(formData, null, true)),
  });

  const onSubmit = (e) => {
    e.preventDefault();

    const changes = diffFields(VEHICLE_TYPE_FIELDS, savedFormData.current || {}, formData);

    if (changes.length === 0) {
      dispatch(setAlert("Nothing to update -- this vehicle type is unchanged.", "primary"));
      return;
    }

    save.request(changes);
  };

  const priceField = (name, label, hint) => (
    <AdminTextField
      name={name}
      label={label}
      hint={hint}
      type="number"
      min="0"
      step="1"
      placeholder="Enter amount"
      value={formData[name]}
      onChange={onChange}
      required
    />
  );

  return (
    <AdminPage
      title="Update Vehicle Type"
      actions={<AdminBackLink to={VEHICLE_TYPE_LIST} />}
    >
      <form onSubmit={onSubmit} className="tw:max-w-[980px]">
        <AdminFormCard title="Vehicle details" columns={2}>
          <AdminTextField
            name="typename"
            label="Type name"
            placeholder="Enter vehicle type"
            value={formData.typename}
            onChange={onChange}
            required
          />

          <AdminSelectField
            name="vehicletypeimage"
            label="Image name"
            options={vehicleTypeImageOptions}
            value={
              vehicleTypeImageOptions.find((o) => o.label === formData.vehicletypeimage) || null
            }
            onChange={(option) => setFormData({ ...formData, vehicletypeimage: option.label })}
          />

          <AdminFormFullRow>
            <AdminTextAreaField
              name="description"
              label="Description"
              placeholder="Enter vehicle type description"
              value={formData.description}
              onChange={onChange}
              required
            />
          </AdminFormFullRow>

          <AdminTextField
            name="maxpassenger"
            label="Maximum passengers"
            type="number"
            min="0"
            step="1"
            placeholder="Enter passengers"
            value={formData.maxpassenger}
            onChange={onChange}
            required
          />

          <AdminTextField
            name="maxluggages"
            label="Maximum luggages"
            type="number"
            min="0"
            step="1"
            placeholder="Enter luggages"
            value={formData.maxluggages}
            onChange={onChange}
            required
          />

          <AdminFormFullRow>
            <AdminTextField
              name="lastAvailibility"
              label="Last availability"
              type="date"
              hint="Optional. The last date this vehicle type can be booked for — it will not be offered for travel dates after this. Leave empty for no limit."
              value={formData.lastAvailibility}
              onChange={onChange}
            />
          </AdminFormFullRow>
        </AdminFormCard>

        <AdminFormCard title="Pricing inside Tokyo" columns={2}>
          {priceField("hourlyprice", "30 minutes rate")}
          {priceField("baseprice", "Base price")}
          {priceField("basepricehours", "Base price hours")}
        </AdminFormCard>

        <AdminFormCard title="Pricing outside Tokyo" columns={2}>
          {priceField("hourlypriceOutsideTokyo", "30 minutes rate")}
          {priceField("basepriceOutsideTokyo", "Base price")}
          {priceField("basepricehoursOutsideTokyo", "Base price hours")}
        </AdminFormCard>

        <AdminFormCard
          title="Vehicle description"
          description="Example of how to enter English and Japanese text copy for the amenities: 9 suitcases / スーツケース9個"
          columns={2}
        >
          <AdminFormFullRow>
            <AdminTextField
              name="subtitle"
              label="Sub-title"
              placeholder="Enter subtitle"
              value={formData.subtitle}
              onChange={onChange}
            />
          </AdminFormFullRow>

          {AMENITY_SLOTS.map((n) => (
            <AdminFormFullRow key={n}>
              <div className="tw:grid tw:gap-x-[20px] tw:gap-y-[8px] tw:md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
                <AdminSelectField
                  name={`option${n}Icon`}
                  label={`Option ${n} icon`}
                  options={iconTypes}
                  value={iconTypes.find((o) => o.label === formData[`option${n}Icon`]) || null}
                  onChange={(option) =>
                    setFormData({ ...formData, [`option${n}Icon`]: option.label })
                  }
                />

                <AdminTextField
                  name={`optionText${n}`}
                  label={`Option ${n} text`}
                  placeholder={`Enter option ${n} text`}
                  value={formData[`optionText${n}`]}
                  onChange={onChange}
                />
              </div>
            </AdminFormFullRow>
          ))}
        </AdminFormCard>

        <AdminFormActions>
          <AdminFormButton type="submit">Update</AdminFormButton>
        </AdminFormActions>
      </form>

      <Alert />

      <ConfirmationModal
        show={!!save.summary}
        title="Apply these vehicle type changes?"
        description="These take effect straight away and are what the customer site quotes from here on."
        confirmLabel="Update vehicle type"
        // The only three-click gate in the dashboard: this is the one form whose
        // numbers are quoted to customers the moment they are saved.
        confirmClicks={3}
        busyLabel="Saving..."
        isBusy={save.isSaving}
        error={save.error}
        onCancel={save.cancel}
        onConfirm={save.confirm}
      >
        <ChangeList changes={save.summary || []} />
      </ConfirmationModal>

      <SuccessModal
        show={!!save.saved}
        title="Vehicle type updated"
        description={`${(save.saved?.length ?? 0) === 1
          ? "This change is"
          : `These ${save.saved?.length ?? 0} changes are`
          } live now. Bookings already placed keep the prices they were quoted.`}
        onClose={save.dismiss}
      >
        <ChangeList changes={save.saved || []} />
      </SuccessModal>
    </AdminPage>
  );
};

export default withRouter(UpdateVehicleType);
